/**
 * LivenessAggregator — Pure domain state machine for liveness verdict smoothing
 *
 * Converts per-frame liveness scores into aggregated verdicts (live | spoof | inconclusive | pending)
 * using a configurable sliding window and consecutive-spoof detector. Eliminates single-frame veto.
 *
 * Zero infrastructure dependencies. Fully testable in isolation.
 */

export interface AggregatorConfig {
  /** Score >= this is "live" frame (default: 0.85) */
  passiveHigh: number;
  /** Score <= this is "spoof" frame (default: 0.40) */
  passiveLow: number;
  /** Circular buffer size (default: 5) */
  windowSize: number;
  /** N live frames in window needed for live verdict (default: 3) */
  liveCount: number;
  /** K consecutive spoof frames for terminal spoof (default: 3) */
  spoofConsecutive: number;
  /** Total frames before inconclusive (default: 30) */
  maxTotalFrames: number;
}

export const DEFAULT_AGGREGATOR_CONFIG: AggregatorConfig = {
  passiveHigh: 0.85,
  passiveLow: 0.40,
  windowSize: 5,
  liveCount: 3,
  spoofConsecutive: 3,
  maxTotalFrames: 30,
};

export type LivenessDecision =
  | { verdict: 'live' }
  | { verdict: 'spoof'; consecutiveCount: number }
  | { verdict: 'inconclusive'; totalFrames: number }
  | { verdict: 'pending'; bufferSize: number };

type Verdict = 'live' | 'spoof' | 'inconclusive';

export class LivenessAggregator {
  private buffer: number[] = [];
  private consecutiveSpoof = 0;
  private totalFrames = 0;
  private terminalVerdict: Verdict | null = null;
  private frozenDecision: LivenessDecision | null = null;

  constructor(private readonly config: AggregatorConfig) {}

  /**
   * Push a single frame's liveness score and get the current verdict.
   *
   * @param score - Liveness score in range [0, 1]
   * @returns LivenessDecision (live | spoof | inconclusive | pending)
   * @throws Error if score is invalid (non-finite or out of range)
   */
  push(score: number): LivenessDecision {
    // Terminal stickiness: once terminal, always return the same decision
    if (this.terminalVerdict !== null && this.frozenDecision !== null) {
      return this.frozenDecision;
    }

    // Input validation
    this.validateScore(score);

    // Increment total frames counter
    this.totalFrames++;

    // Classify frame
    const isSpoof = score <= this.config.passiveLow;

    // Spoof streak detection (BEFORE adding to buffer, check consecutive)
    if (isSpoof) {
      this.consecutiveSpoof++;
      if (this.consecutiveSpoof >= this.config.spoofConsecutive) {
        const decision: LivenessDecision = {
          verdict: 'spoof',
          consecutiveCount: this.consecutiveSpoof,
        };
        this.terminalVerdict = 'spoof';
        this.frozenDecision = decision;
        return decision;
      }
    } else {
      this.consecutiveSpoof = 0; // Reset on non-spoof
    }

    // Add score to circular buffer
    this.buffer.push(score);
    if (this.buffer.length > this.config.windowSize) {
      this.buffer.shift();
    }

    // Live consensus: check if last liveCount frames are ALL >= passiveHigh
    if (this.buffer.length >= this.config.liveCount) {
      const lastN = this.buffer.slice(-this.config.liveCount);
      const allLive = lastN.every((s) => s >= this.config.passiveHigh);
      if (allLive) {
        const decision: LivenessDecision = { verdict: 'live' };
        this.terminalVerdict = 'live';
        this.frozenDecision = decision;
        return decision;
      }
    }

    // Timeout: max frames reached without decision
    if (this.totalFrames >= this.config.maxTotalFrames) {
      const decision: LivenessDecision = {
        verdict: 'inconclusive',
        totalFrames: this.totalFrames,
      };
      this.terminalVerdict = 'inconclusive';
      this.frozenDecision = decision;
      return decision;
    }

    // Pending: not enough evidence yet
    return { verdict: 'pending', bufferSize: this.buffer.length };
  }

  /**
   * Reset all internal state (buffer, counters, terminal verdict).
   * Aggregator behaves like new after reset.
   */
  reset(): void {
    this.buffer = [];
    this.consecutiveSpoof = 0;
    this.totalFrames = 0;
    this.terminalVerdict = null;
    this.frozenDecision = null;
  }

  /**
   * Validate score: must be finite number in [0, 1]
   */
  private validateScore(score: number): void {
    if (!Number.isFinite(score)) {
      throw new Error(
        `Invalid liveness score: must be finite number, got ${score} (${typeof score})`
      );
    }
    if (score < 0 || score > 1) {
      throw new Error(
        `Invalid liveness score: must be in range [0, 1], got ${score}`
      );
    }
  }
}
